import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth tokens
api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem('admin_token') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('📤 [API REQUEST]', config.method?.toUpperCase(), config.url, config.data);
    return config;
  },
  (error) => {
    console.error('📤 [API REQUEST ERROR]', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging
api.interceptors.response.use(
  (response) => {
    console.log('📥 [API RESPONSE]', response.config.method?.toUpperCase(), response.config.url, response.status, response.data);
    return response;
  },
  (error) => {
    console.error('📥 [API RESPONSE ERROR]', error.config?.method?.toUpperCase(), error.config?.url, error.response?.status, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Admin API methods
export const adminAPI = {
  login: (email, password) => api.post('/admin/auth/login', { email, password }),
  getProfile: () => api.get('/admin/auth/me'),
  logout: () => api.post('/admin/auth/logout'),

  // Dashboard stats
  getDashboardStats: async () => {
    try {
      // Get real stats from API
        const [usersRes, jobsRes, paymentsRes, refEventsRes] = await Promise.allSettled([
        api.get('/admin/users'),
        api.get('/admin/jobs'),
        api.get('/admin/payments'),
        api.get('/admin/referral-events'),
      ]);

      const usersData = usersRes.status === 'fulfilled' ? usersRes.value.data : { users: [] };
      const jobsData = jobsRes.status === 'fulfilled' ? jobsRes.value.data : { jobs: [] };
      const paymentsData = paymentsRes.status === 'fulfilled' ? paymentsRes.value.data : { payments: [] };
      const refEventsData = refEventsRes.status === 'fulfilled' ? refEventsRes.value.data : { events: [] };

      // Calculate stats
      const totalUsers = usersData.total || 0;
      const jobs = Array.isArray(jobsData.jobs) ? jobsData.jobs : [];
      const activeJobs = jobs.filter(j => j.status === 'running' || j.status === 'queued').length;
      
      // Calculate monthly revenue
      const currentMonth = new Date().getMonth();
      const payments = Array.isArray(paymentsData.payments) ? paymentsData.payments : [];
      const monthlyPayments = payments.filter(p => {
        const paymentDate = new Date(p.created_at);
        return paymentDate.getMonth() === currentMonth && p.status === 'completed';
      });
      const monthlyRevenue = monthlyPayments.reduce((sum, p) => sum + (p.amount_rub || 0), 0);

      // Calculate referrals count
      const events = Array.isArray(refEventsData.events) ? refEventsData.events : [];
      const uniqueRefs = new Set(events.filter(e => e.event === 'signup').map(e => e.user_id)).size;

      return {
        totalUsers,
        activeJobs,
        monthlyRevenue,
        referrals: uniqueRefs,
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Return fallback stats
      return {
        totalUsers: 0,
        activeJobs: 0,
        monthlyRevenue: 0,
        referrals: 0,
      };
    }
  },

  // Users
  getUsers: (config = {}) => api.get('/admin/users', config),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  getUserSubscriptions: (userId) => api.get(`/admin/users/${userId}/subscriptions`),
  getUserPackages: (userId) => api.get(`/admin/users/${userId}/packages`),
  banUser: (userId, reason) => api.put(`/admin/users/${userId}/ban`, { reason }),
  unbanUser: (userId) => api.put(`/admin/users/${userId}/unban`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  adjustBalance: async (userId, amount, reason) => {
    console.log('API: adjustBalance called with', { userId, amount, reason });
    try {
      const response = await api.put(`/admin/users/${userId}/balance`, { amount, reason });
      console.log('API: adjustBalance response', response.data);
      return response;
    } catch (error) {
      console.error('API: adjustBalance error', error);
      throw error;
    }
  },
  updateUser: (userId, userData) => api.put(`/admin/users/${userId}`, userData),
  getUserJobs: (userId, config = {}) => api.get(`/admin/jobs`, { params: { user_id: userId, ...config.params } }),
  getUserPayments: (userId, config = {}) => api.get(`/admin/payments`, { params: { user_id: userId, ...config.params } }),
  
  // Jobs
  getJobs: (config = {}) => api.get('/admin/jobs', config),
  getJobDetails: (jobId) => api.get(`/admin/jobs/${jobId}`),
  cancelJob: (jobId, reason) => api.post(`/admin/jobs/${jobId}/cancel`, { reason }),
  retryJob: (jobId) => api.post(`/admin/jobs/${jobId}/retry`),
  
  // Payments
  getPayments: ({ limit = 100, offset = 0, status, provider, userId } = {}) =>
    api.get('/admin/payments', {
      params: {
        limit,
        offset,
        status,
        provider,
        user_id: userId,
      },
    }),
  getBillingOverview: () => api.get('/admin/billing/overview'),
  getBillingPlans: () => api.get('/admin/billing/plans'),
  createBillingPlan: (payload) => api.post('/admin/billing/plans', payload),
  updateBillingPlan: (planId, payload) => api.put(`/admin/billing/plans/${planId}`, payload),
  deleteBillingPlan: (planId) => api.delete(`/admin/billing/plans/${planId}`),
  getBillingPackages: () => api.get('/admin/billing/packages'),
  upsertBillingPackage: (payload) => api.post('/admin/billing/packages', payload),
  deleteBillingPackage: (packageId) => api.delete(`/admin/billing/packages/${packageId}`),
  upsertPackageOption: (packageId, payload) =>
    api.post(`/admin/billing/packages/${packageId}/options`, payload),
  deletePackageOption: (optionId) =>
    api.delete(`/admin/billing/packages/options/${optionId}`),
  getBillingSubscriptions: (params = {}) =>
    api.get('/admin/billing/subscriptions', { params }),
  updateBillingSubscriptionStatus: (subscriptionId, status, reason) =>
    api.put(`/admin/billing/subscriptions/${subscriptionId}/status`, {
      status,
      reason,
    }),
  getUserBalanceAdmin: (userId) => api.get(`/admin/billing/balance/${userId}`),
  creditUserBalance: (userId, amount, reason) =>
    api.post('/admin/billing/credit', { user_id: userId, amount, reason }),
  debitUserBalance: (userId, amount, reason) =>
    api.post('/admin/billing/debit', { user_id: userId, amount, reason }),
  
  // Referral events
  getReferralEvents: (config = {}) => api.get('/admin/referral-events', config),
  
  // Providers
  getProviders: (config = {}) => api.get('/admin/providers', config),
  updateProvider: (providerId, data) => api.put(`/admin/providers/${providerId}`, data),
  createProvider: (data) => api.post('/admin/providers', data),
  deleteProvider: (providerId) => api.delete(`/admin/providers/${providerId}`),
  
  // Bot Actions
  getBotActions: (config = {}) => api.get('/admin/bot-actions', config),
  getBotAction: (actionKey, config = {}) => api.get(`/admin/bot-actions/${actionKey}`, config),
  createBotAction: (data) => api.post('/admin/bot-actions', data),
  updateBotAction: (actionId, data) => api.put(`/admin/bot-actions/${actionId}`, data),
  deleteBotAction: (actionId) => api.delete(`/admin/bot-actions/${actionId}`),
  
  // System health
  getSystemHealth: (config = {}) => api.get('/admin/system-health', config),
  
  // Audit logs
  getAuditLogs: (config = {}) => api.get('/admin/audit-logs', config),
  
  // Dashboard stats
  getRevenueStats: (config = {}) => api.get('/admin/stats/revenue', config),
  getUserStats: (config = {}) => api.get('/admin/stats/users', config),
  getRecentActivity: (limit = 10) => api.get(`/admin/stats/activity`, { params: { limit } }),

  // Admin users
  getAdminUsers: (params = {}) => api.get('/admin/admin-users', { params }),
  createAdminUser: (payload) => api.post('/admin/admin-users', payload),
  updateAdminUser: (adminId, payload) => api.put(`/admin/admin-users/${adminId}`, payload),
  updateAdminPassword: (adminId, payload) => api.post(`/admin/admin-users/${adminId}/password`, payload),
};

export default api;

